import { useState } from 'react'
import { validateInviteCode, applyInviteCode } from '../api/auth'

interface Props {
  isOpen: boolean
  onClose: () => void   // skip — go to onboarding
  onSuccess: () => void // code applied — go to onboarding
}

export default function InviteCodeModal({ isOpen, onClose, onSuccess }: Props) {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen) return null

  async function handleApplyCode() {
    if (!code.trim()) return
    setLoading(true)
    setError('')
    try {
      const validation = await validateInviteCode(code.trim().toUpperCase())
      if (!validation.valid) {
        setError(validation.error ?? 'Invalid or expired code.')
        setLoading(false)
        return
      }
      const result = await applyInviteCode(code.trim().toUpperCase())
      if (result.success) {
        setSuccess(true)
        setTimeout(() => onSuccess(), 1500)
      } else {
        setError((result as any).message || (result as any).error || 'Something went wrong. Please try again.')
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div className="invite-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="invite-modal-emoji">🎉</div>
        <h2 className="invite-modal-title">Welcome to Aspired AI!</h2>
        <p className="invite-modal-sub">
          Have an invite code? Enter it now to unlock beta access to all agents.
        </p>

        {success ? (
          <div className="invite-modal-success">
            ✓ Beta access unlocked! All agents are yours.
          </div>
        ) : (
          <>
            <div className="invite-modal-input-group">
              <input
                className="invite-modal-input"
                type="text"
                placeholder="Enter invite code e.g. BETA2026"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setError('') }}
                style={{ textTransform: 'uppercase' }}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCode()}
              />
              <button
                className="btn-primary invite-modal-btn"
                onClick={handleApplyCode}
                disabled={loading || !code.trim()}
              >
                {loading ? 'Applying…' : 'Apply'}
              </button>
            </div>
            {error && <div className="invite-modal-error">{error}</div>}
            <button className="invite-modal-skip" onClick={onClose}>
              Skip for now — enter code later in Settings
            </button>
          </>
        )}
      </div>
    </div>
  )
}
